import React, { createContext, useContext, useCallback, useState } from 'react';
import { useTranslation } from 'src/text';
import { generateSocialPostViaVertex } from 'src/api/services/ai-service';
import { ALLOWED_RATIOS, DEFAULT_BRAND_GUIDELINES } from './constants';
import {
  AspectRatio,
  CampaignsContextValue,
  ImageQuality,
  MediaSource,
  Platform,
  Tone,
} from './types';
import type { Mode } from './types';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';
import { useAppAuth } from 'src/context/use-app-auth';
import { getImageQuotaStatus } from 'src/api/services/usage-service';

const CampaignsContext = createContext<CampaignsContextValue | undefined>(
  undefined,
);

export const CampaignsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useTranslation();
  const { location, user } = useAppAuth();

  // Primary UI state
  const [platform, setPlatform] = useState<Platform>('whatsapp');
  const [mode, setMode] = useState<Mode>('url');
  const [sourceUrl, setSourceUrl] = useState('');
  const [freeText, setFreeText] = useState('');
  const [hashtagsLimit, setHashtagsLimit] = useState<number>(5);
  const [tone, setTone] = useState<Tone>('friendly');
  const [allowEmoji, setAllowEmoji] = useState<boolean>(true);
  const [media, setMedia] = useState<MediaSource>('none');
  const [mediaUrl, setMediaUrl] = useState('');
  const [approxChars, setApproxChars] = useState<number>(60);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [imageQuality, setImageQuality] = useState<ImageQuality>('standard');
  const [guidelines, setGuidelines] = useState<string>(
    DEFAULT_BRAND_GUIDELINES,
  );

  // Ensure aspect ratio stays valid when platform changes
  React.useEffect(() => {
    const options = ALLOWED_RATIOS[platform];
    if (!options.includes(aspectRatio)) {
      setAspectRatio(options[0]);
    }
  }, [platform]);

  // Generation result state
  const [jsonOutput, setJsonOutput] = useState<
    GeneratedSocialPostResult | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Image quota state (per location per billing cycle)
  const [quota, setQuota] = useState<
    | {
        used: number;
        limit: number | 'unlimited';
        remaining: number | 'unlimited';
        loading: boolean;
      }
    | undefined
  >(undefined);
  const refreshQuota = useCallback(async () => {
    if (!location?.key) return;
    setQuota(prev => ({
      used: prev?.used || 0,
      limit: prev?.limit || 30,
      remaining: prev?.remaining || 0,
      loading: true,
    }));
    try {
      if (!user?.key) return;
      const q = await getImageQuotaStatus(
        location.key,
        user.key,
        undefined,
        undefined,
        undefined,
      );
      setQuota({ ...q, loading: false });
    } catch {
      setQuota(prev => (prev ? { ...prev, loading: false } : undefined));
    }
  }, [location?.key, user?.key]);

  // Load quota when location changes or AI media selected
  React.useEffect(() => {
    if (media === 'ai') refreshQuota();
  }, [location?.key, media, refreshQuota]);

  const resetOutput = () => {
    setJsonOutput(undefined);
  };

  const generate = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);

    try {
      // Map UI media selection to server imageOption
      const imageOption = (() => {
        switch (media) {
          case 'ai':
            return {
              mode: 'generate' as const,
              ratio: aspectRatio,
              quality: imageQuality,
            };
          case 'url':
            return { mode: 'link' as const, linkUrl: mediaUrl || undefined };
          case 'upload':
            // Uploads are stored in Firebase Storage and we get a download URL,
            // so treat them like link-based images on the backend.
            return { mode: 'upload' as const, linkUrl: mediaUrl || undefined };
          default:
            return undefined;
        }
      })();

      // Guard: if generating AI image, ensure quota allows it
      if (media === 'ai') {
        if (!location?.key) throw new Error('No location selected');
        if (quota && quota.remaining !== 'unlimited' && quota.remaining <= 0) {
          throw new Error('Image generation limit reached for this cycle.');
        }
      }

      const result = await generateSocialPostViaVertex({
        platform,
        mode,
        sourceUrl: mode === 'url' ? sourceUrl : undefined,
        freeText: mode === 'text' ? freeText : undefined,
        hashtagsLimit,
        tone,
        allowEmoji,
        approxChars,
        guidelines,
        imageOption,
        locationKey: location?.key,
        userId: user?.key,
        // topic can be derived from freeText if needed; omit for now
      });

      setJsonOutput(result);
      if (media === 'ai') refreshQuota();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [
    platform,
    mode,
    sourceUrl,
    freeText,
    hashtagsLimit,
    tone,
    allowEmoji,
    media,
    mediaUrl,
    approxChars,
    guidelines,
    location?.key,
    quota?.remaining,
    refreshQuota,
    aspectRatio,
    imageQuality,
    loading,
  ]);

  const value: CampaignsContextValue = {
    platform,
    setPlatform,
    mode,
    setMode,
    sourceUrl,
    setSourceUrl,
    freeText,
    setFreeText,
    hashtagsLimit,
    setHashtagsLimit,
    tone,
    setTone,
    allowEmoji,
    setAllowEmoji,
    media,
    setMedia,
    mediaUrl,
    setMediaUrl,
    approxChars,
    setApproxChars,
    aspectRatio,
    setAspectRatio,
    imageQuality,
    setImageQuality,
    guidelines,
    setGuidelines,
    jsonOutput,
    loading,
    error,
    generate,
    resetOutput,
    quota,
  };

  return (
    <CampaignsContext.Provider value={value}>
      {children}
    </CampaignsContext.Provider>
  );
};

export const useCampaigns = (): CampaignsContextValue => {
  const ctx = useContext(CampaignsContext);
  if (!ctx)
    throw new Error('useCampaigns must be used within CampaignsProvider');
  return ctx;
};
